import { SectionSummaryHSPVariant } from "@mstys/raport-auta-client-front/components";
import {
  WarningsAndRecommendations,
  WarningItem,
  ProblemStatus,
  RiskSeverity,
} from "./report";

/**
 * Interfejs reprezentujący zmapowane ostrzeżenie/rekomendację
 */
export interface MappedWarning {
  variant: SectionSummaryHSPVariant;
  title: string;
  content: string;
  recommendation?: string;
  severity: RiskSeverity;
  problemStatus: ProblemStatus;
}

type TranslationFunction = (key: string, defaultValue?: string) => string;

/**
 * Typ definiujący szczegóły dla każdego kodu ostrzeżenia
 */
interface WarningCodeDetails {
  getTitle: (status: ProblemStatus, t?: TranslationFunction) => string;
  getContent: (status: ProblemStatus, t?: TranslationFunction) => string;
  getRecommendation: (
    status: ProblemStatus,
    t?: TranslationFunction
  ) => string | undefined;
}

/**
 * Mapa kodów ostrzeżeń na ich szczegóły
 */
const warningCodeMap: Record<string, WarningCodeDetails> = {
  TotalLossRecorded: {
    getTitle: (status: ProblemStatus, t?: TranslationFunction) => {
      if (t) {
        switch (status) {
          case "Exists":
            return t(
              "warnings.TotalLossRecorded.title_exists",
              "Brak szkód całkowitych"
            );
          case "NotExists":
            return t(
              "warnings.TotalLossRecorded.title_not_exists",
              "Nie wykryto wpisów o szkodzie całkowitej."
            );
          case "MissingData":
            return t(
              "warnings.TotalLossRecorded.title_missing_data",
              "Brak wystarczających danych do weryfikacji czy pojazd ma szkodę całkowitą."
            );
        }
      }
      switch (status) {
        case "Exists":
          return "Brak szkód całkowitych";
        case "NotExists":
          return "Nie wykryto wpisów o szkodzie całkowitej.";
        case "MissingData":
          return "Brak wystarczających danych do weryfikacji czy pojazd ma szkodę całkowitą.";
      }
    },
    getContent: (status, t?) => {
      if (t) {
        switch (status) {
          case "Exists":
            return t(
              "warnings.TotalLossRecorded.content_exists",
              "Pojazd został uznany za całkowitą stratę ekonomiczną. Koszt naprawy przekroczył wartość rynkową pojazdu w momencie szkody."
            );
          case "NotExists":
            return t(
              "warnings.TotalLossRecorded.content_not_exists",
              "Nie wykryto wpisów o szkodzie całkowitej. To dobry znak dla wartości pojazdu."
            );
          case "MissingData":
            return "";
        }
      }
      switch (status) {
        case "Exists":
          return "Pojazd został uznany za całkowitą stratę ekonomiczną. Koszt naprawy przekroczył wartość rynkową pojazdu w momencie szkody.";
        case "NotExists":
          return "Nie wykryto wpisów o szkodzie całkowitej. To dobry znak dla wartości pojazdu.";
        case "MissingData":
          return "";
      }
    },
    getRecommendation: (status, t?) => {
      if (t) {
        switch (status) {
          case "Exists":
            return t(
              "warnings.TotalLossRecorded.recommendation_exists",
              "KRYTYCZNE: Sprawdź zakres napraw i aktualny stan techniczny pojazdu. Rozważ szczegółową ekspertyzę."
            );
          case "NotExists":
            return undefined;
          case "MissingData":
            return t(
              "warnings.TotalLossRecorded.recommendation_missing_data",
              "Zweryfikuj historię szkód w krajowych bazach danych."
            );
        }
      }
      switch (status) {
        case "Exists":
          return "KRYTYCZNE: Sprawdź zakres napraw i aktualny stan techniczny pojazdu. Rozważ szczegółową ekspertyzę.";
        case "NotExists":
          return undefined;
        case "MissingData":
          return "Zweryfikuj historię szkód w krajowych bazach danych.";
      }
    },
  },
  SingleDamageOver60Percent: {
    getTitle: (status: ProblemStatus, t?: TranslationFunction) => {
      if (t) {
        switch (status) {
          case "Exists":
            return t(
              "warnings.SingleDamageOver60Percent.title_exists",
              "Wykryto pojedynczą szkodę przekraczającą 60% wartości"
            );
          case "NotExists":
            return t(
              "warnings.SingleDamageOver60Percent.title_not_exists",
              "Nie wykryto szkód przekraczających 60% wartości"
            );
          case "MissingData":
            return t(
              "warnings.SingleDamageOver60Percent.title_missing_data",
              "Brak wystarczających danych do weryfikacji czy pojazd ma szkodę przekraczającą 60% wartości"
            );
        }
      }
      switch (status) {
        case "Exists":
          return "Wykryto pojedynczą szkodę przekraczającą 60% wartości";
        case "NotExists":
          return "Nie wykryto szkód przekraczających 60% wartości";
        case "MissingData":
          return "Brak wystarczających danych do weryfikacji czy pojazd ma szkodę przekraczającą 60% wartości";
      }
    },
    getContent: (status, t?) => {
      if (t) {
        switch (status) {
          case "Exists":
            return t(
              "warnings.SingleDamageOver60Percent.content_exists",
              "Wykryto co najmniej jedną szkodę, której wartość przekroczyła 60% wartości rynkowej pojazdu. Świadczy to o bardzo poważnych uszkodzeniach."
            );
          case "NotExists":
            return "";
          case "MissingData":
            return "";
        }
      }
      switch (status) {
        case "Exists":
          return "Wykryto co najmniej jedną szkodę, której wartość przekroczyła 60% wartości rynkowej pojazdu. Świadczy to o bardzo poważnych uszkodzeniach.";
        case "NotExists":
          return "";
        case "MissingData":
          return "";
      }
    },
    getRecommendation: (status, t?) => {
      if (t) {
        switch (status) {
          case "Exists":
            return t(
              "warnings.SingleDamageOver60Percent.recommendation_exists",
              "Zbadaj dokładnie zakres naprawy i sprawdź jakość wykonanych prac. Rozważ ekspertyzę mechaniczną."
            );
          case "NotExists":
            return undefined;
          case "MissingData":
            return t(
              "warnings.SingleDamageOver60Percent.recommendation_missing_data",
              "Poproś sprzedawcę o szczegółową dokumentację napraw."
            );
        }
      }
      switch (status) {
        case "Exists":
          return "Zbadaj dokładnie zakres naprawy i sprawdź jakość wykonanych prac. Rozważ ekspertyzę mechaniczną.";
        case "NotExists":
          return undefined;
        case "MissingData":
          return "Poproś sprzedawcę o szczegółową dokumentację napraw.";
      }
    },
  },
  MoreThanThreeDamages: {
    getTitle: (status: ProblemStatus, t?: TranslationFunction) => {
      if (t) {
        switch (status) {
          case "Exists":
            return t(
              "warnings.MoreThanThreeDamages.title_exists",
              "Więcej niż trzy szkody"
            );
          case "NotExists":
            return t(
              "warnings.MoreThanThreeDamages.title_not_exists",
              "Pojazd ma niewielką liczbę szkód w historii."
            );
          case "MissingData":
            return t(
              "warnings.MoreThanThreeDamages.title_missing_data",
              "Brak wystarczających danych do weryfikacji czy pojazd ma więcej niż trzy szkody."
            );
        }
      }
      switch (status) {
        case "Exists":
          return "Więcej niż trzy szkody";
        case "NotExists":
          return "Pojazd ma niewielką liczbę szkód w historii.";
        case "MissingData":
          return "Brak wystarczających danych do weryfikacji czy pojazd ma więcej niż trzy szkody.";
      }
    },
    getContent: (status, t?) => {
      if (t) {
        switch (status) {
          case "Exists":
            return t(
              "warnings.MoreThanThreeDamages.content_exists",
              "Pojazd ma odnotowane więcej niż trzy szkody w swojej historii. Może to wskazywać na intensywną eksploatację lub problemy z jakością napraw."
            );
          case "NotExists":
            return t(
              "warnings.MoreThanThreeDamages.content_not_exists",
              "Zalecamy szczegółowe zapoznanie się z pełną historią pojazdu, a zwłaszcza z sekcją 'Historia Szkód Pojazdu', aby uzyskać kompletny obraz jego przeszłości."
            );
          case "MissingData":
            return t(
              "warnings.MoreThanThreeDamages.content_missing_data",
              "Brak kompletnych danych o historii szkód pojazdu."
            );
        }
      }
      switch (status) {
        case "Exists":
          return "Pojazd ma odnotowane więcej niż trzy szkody w swojej historii. Może to wskazywać na intensywną eksploatację lub problemy z jakością napraw.";
        case "NotExists":
          return "Zalecamy szczegółowe zapoznanie się z pełną historią pojazdu, a zwłaszcza z sekcją 'Historia Szkód Pojazdu', aby uzyskać kompletny obraz jego przeszłości.";
        case "MissingData":
          return "Brak kompletnych danych o historii szkód pojazdu.";
      }
    },
    getRecommendation: (status, t?) => {
      if (t) {
        switch (status) {
          case "Exists":
            return t(
              "warnings.MoreThanThreeDamages.recommendation_exists",
              "Dokładnie przeanalizuj każdą szkodę i sprawdź jakość napraw. Zwróć uwagę na lokalizację uszkodzeń."
            );
          case "NotExists":
            return undefined;
          case "MissingData":
            return t(
              "warnings.MoreThanThreeDamages.recommendation_missing_data",
              "Sprawdź historię w alternatywnych bazach danych lub u ubezpieczyciela."
            );
        }
      }
      switch (status) {
        case "Exists":
          return "Dokładnie przeanalizuj każdą szkodę i sprawdź jakość napraw. Zwróć uwagę na lokalizację uszkodzeń.";
        case "NotExists":
          return undefined;
        case "MissingData":
          return "Sprawdź historię w alternatywnych bazach danych lub u ubezpieczyciela.";
      }
    },
  },
  OdometerRollbackDetected: {
    getTitle: (status: ProblemStatus, t?: TranslationFunction) => {
      if (t) {
        switch (status) {
          case "Exists":
            return t(
              "warnings.OdometerRollbackDetected.title_exists",
              "Wykryto cofnięcie licznika"
            );
          case "NotExists":
            return t(
              "warnings.OdometerRollbackDetected.title_not_exists",
              "Nie wykryto cofnięcia licznika"
            );
          case "MissingData":
            return t(
              "warnings.OdometerRollbackDetected.title_missing_data",
              "Brak wystarczających danych do weryfikacji czy pojazd ma cofnięcie licznika."
            );
        }
      }
      switch (status) {
        case "Exists":
          return "Wykryto cofnięcie licznika";
        case "NotExists":
          return "Nie wykryto cofnięcia licznika";
        case "MissingData":
          return "Brak wystarczających danych do weryfikacji czy pojazd ma cofnięcie licznika.";
      }
    },
    getContent: (status, t?) => {
      if (t) {
        switch (status) {
          case "Exists":
            return t(
              "warnings.OdometerRollbackDetected.content_exists",
              "Odnotowano rozbieżności w zapisach przebiegu pojazdu. Różnice między kolejnymi pomiarami wskazują na możliwą manipulację licznika."
            );
          case "NotExists":
            return t(
              "warnings.OdometerRollbackDetected.content_not_exists",
              "Przebieg pojazdu jest zgodny w kolejnych pomiarach. Nie wykryto nieprawidłowości."
            );
          case "MissingData":
            return t(
              "warnings.OdometerRollbackDetected.content_missing_data",
              "Brak wystarczającej liczby pomiarów przebiegu do przeprowadzenia pełnej weryfikacji."
            );
        }
      }
      switch (status) {
        case "Exists":
          return "Odnotowano rozbieżności w zapisach przebiegu pojazdu. Różnice między kolejnymi pomiarami wskazują na możliwą manipulację licznika.";
        case "NotExists":
          return "Przebieg pojazdu jest zgodny w kolejnych pomiarach. Nie wykryto nieprawidłowości.";
        case "MissingData":
          return "Brak wystarczającej liczby pomiarów przebiegu do przeprowadzenia pełnej weryfikacji.";
      }
    },
    getRecommendation: (status, t?) => {
      if (t) {
        switch (status) {
          case "Exists":
            return t(
              "warnings.OdometerRollbackDetected.recommendation_exists",
              "UWAGA: Manipulacja przebiegiem jest przestępstwem. Zweryfikuj przebieg u autoryzowanego dealera i sprawdź stan techniczny."
            );
          case "NotExists":
            return undefined;
          case "MissingData":
            return t(
              "warnings.OdometerRollbackDetected.recommendation_missing_data",
              "Przeprowadź szczegółową weryfikację historii przebiegu w dostępnych bazach danych."
            );
        }
      }
      switch (status) {
        case "Exists":
          return "UWAGA: Manipulacja przebiegiem jest przestępstwem. Zweryfikuj przebieg u autoryzowanego dealera i sprawdź stan techniczny.";
        case "NotExists":
          return undefined;
        case "MissingData":
          return "Przeprowadź szczegółową weryfikację historii przebiegu w dostępnych bazach danych.";
      }
    },
  },
  StolenVehicleHit: {
    getTitle: (status: ProblemStatus, t?: TranslationFunction) => {
      if (t) {
        switch (status) {
          case "Exists":
            return t(
              "warnings.StolenVehicleHit.title_exists",
              "Pojazd znajduje się w bazie pojazdów skradzionych"
            );
          case "NotExists":
            return t(
              "warnings.StolenVehicleHit.title_not_exists",
              "Pojazd nie widnieje w bazach pojazdów skradzionych"
            );
          case "MissingData":
            return t(
              "warnings.StolenVehicleHit.title_missing_data",
              "Brak wystarczających danych do weryfikacji czy pojazd jest skradziony."
            );
        }
      }
      switch (status) {
        case "Exists":
          return "Pojazd znajduje się w bazie pojazdów skradzionych";
        case "NotExists":
          return "Pojazd nie widnieje w bazach pojazdów skradzionych";
        case "MissingData":
          return "Brak wystarczających danych do weryfikacji czy pojazd jest skradziony.";
      }
    },
    getContent: (status, t?) => {
      if (t) {
        switch (status) {
          case "Exists":
            return t(
              "warnings.StolenVehicleHit.content_exists",
              "Odnotowano wpis w bazie pojazdów skradzionych, co jest sygnałem wymagającym najwyższej ostrożności. Taka sytuacja wiąże się z ryzykiem prawnym dla osoby kupującej pojazd."
            );
          case "NotExists":
            return t(
              "warnings.StolenVehicleHit.content_not_exists",
              "Pojazd nie widnieje w bazach pojazdów skradzionych. Jest to potwierdzenie legalnego pochodzenia."
            );
          case "MissingData":
            return t(
              "warnings.StolenVehicleHit.content_missing_data",
              "Nie udało się zweryfikować pojazdu w bazach pojazdów skradzionych."
            );
        }
      }
      switch (status) {
        case "Exists":
          return "Odnotowano wpis w bazie pojazdów skradzionych, co jest sygnałem wymagającym najwyższej ostrożności. Taka sytuacja wiąże się z ryzykiem prawnym dla osoby kupującej pojazd.";
        case "NotExists":
          return "Pojazd nie widnieje w bazach pojazdów skradzionych. Jest to potwierdzenie legalnego pochodzenia.";
        case "MissingData":
          return "Nie udało się zweryfikować pojazdu w bazach pojazdów skradzionych.";
      }
    },
    getRecommendation: (status, t?) => {
      if (t) {
        switch (status) {
          case "Exists":
            return t(
              "warnings.StolenVehicleHit.recommendation_exists",
              "Zalecamy wstrzymanie transakcji do momentu pełnego wyjaśnienia sprawy. Najlepszym krokiem będzie kontakt z odpowiednimi organami ścigania w celu oficjalnej weryfikacji statusu pojazdu, co pozwoli uniknąć poważnych komplikacji."
            );
          case "NotExists":
            return undefined;
          case "MissingData":
            return t(
              "warnings.StolenVehicleHit.recommendation_missing_data",
              "Przed zakupem samodzielnie sprawdź pojazd w krajowych i międzynarodowych bazach."
            );
        }
      }
      switch (status) {
        case "Exists":
          return "KRYTYCZNE: NIE KUPUJ tego pojazdu! Zgłoś sprawę na Policję i powiadom odpowiednie służby.";
        case "NotExists":
          return undefined;
        case "MissingData":
          return "Przed zakupem samodzielnie sprawdź pojazd w krajowych i międzynarodowych bazach.";
      }
    },
  },
  TaxLienRegistered: {
    getTitle: (status: ProblemStatus, t?: TranslationFunction) => {
      if (t) {
        switch (status) {
          case "Exists":
            return t(
              "warnings.TaxLienRegistered.title_exists",
              "Zarejestrowane obciążenie podatkowe/zastaw"
            );
          case "NotExists":
            return t(
              "warnings.TaxLienRegistered.title_not_exists",
              "Nie wykryto obciążeń ani zastawów."
            );
          case "MissingData":
            return t(
              "warnings.TaxLienRegistered.title_missing_data",
              "Brak wystarczających danych do weryfikacji czy pojazd ma obciążenia podatkowe lub zastawy."
            );
        }
      }
      switch (status) {
        case "Exists":
          return "Zarejestrowane obciążenie podatkowe/zastaw";
        case "NotExists":
          return "Nie wykryto obciążeń ani zastawów.";
        case "MissingData":
          return "Brak wystarczających danych do weryfikacji czy pojazd ma obciążenia podatkowe lub zastawy.";
      }
    },
    getContent: (status, t?) => {
      if (t) {
        switch (status) {
          case "Exists":
            return t(
              "warnings.TaxLienRegistered.content_exists",
              "Na pojeździe ciąży obciążenie podatkowe lub zastaw. Kupując taki pojazd możesz przejąć odpowiedzialność za te zobowiązania."
            );
          case "NotExists":
            return t(
              "warnings.TaxLienRegistered.content_not_exists",
              "Pojazd nie ma żadnych obciążeń ani zastawów. Jest czysty prawnie."
            );
          case "MissingData":
            return "";
        }
      }
      switch (status) {
        case "Exists":
          return "Na pojeździe ciąży obciążenie podatkowe lub zastaw. Kupując taki pojazd możesz przejąć odpowiedzialność za te zobowiązania.";
        case "NotExists":
          return "Pojazd nie ma żadnych obciążeń ani zastawów. Jest czysty prawnie.";
        case "MissingData":
          return "";
      }
    },
    getRecommendation: (status, t?) => {
      if (t) {
        switch (status) {
          case "Exists":
            return t(
              "warnings.TaxLienRegistered.recommendation_exists",
              "Przed zakupem zażądaj uregulowania wszystkich zobowiązań i uzyskaj pisemne potwierdzenie."
            );
          case "NotExists":
            return undefined;
          case "MissingData":
            return t(
              "warnings.TaxLienRegistered.recommendation_missing_data",
              "Sprawdź pojazd w rejestrze zastawów oraz u właściwych organów podatkowych."
            );
        }
      }
      switch (status) {
        case "Exists":
          return "Przed zakupem zażądaj uregulowania wszystkich zobowiązań i uzyskaj pisemne potwierdzenie.";
        case "NotExists":
          return undefined;
        case "MissingData":
          return "Sprawdź pojazd w rejestrze zastawów oraz u właściwych organów podatkowych.";
      }
    },
  },
  MoreThanFiveOwnersFiveYears: {
    getTitle: (status: ProblemStatus, t?: TranslationFunction) => {
      if (t) {
        switch (status) {
          case "Exists":
            return t(
              "warnings.MoreThanFiveOwnersFiveYears.title_exists",
              "Więcej niż pięciu właścicieli w ciągu ostatnich pięciu lat"
            );
          case "NotExists":
            return t(
              "warnings.MoreThanFiveOwnersFiveYears.title_not_exists",
              "Pojazd miał stabilną historię właścicieli"
            );
          case "MissingData":
            return t(
              "warnings.MoreThanFiveOwnersFiveYears.title_missing_data",
              "Brak wystarczających danych do weryfikacji czy pojazd ma więcej niż pięciu właścicieli w ciągu ostatnich pięciu lat."
            );
        }
      }
      switch (status) {
        case "Exists":
          return "Więcej niż pięciu właścicieli w ciągu ostatnich pięciu lat";
        case "NotExists":
          return "Pojazd miał stabilną historię właścicieli";
        case "MissingData":
          return "Brak wystarczających danych do weryfikacji czy pojazd ma więcej niż pięciu właścicieli w ciągu ostatnich pięciu lat.";
      }
    },
    getContent: (status, t?) => {
      if (t) {
        switch (status) {
          case "Exists":
            return t(
              "warnings.MoreThanFiveOwnersFiveYears.content_exists",
              "Pojazd miał wielu właścicieli w krótkim czasie. Może to wskazywać na ukrywane problemy techniczne lub prawne."
            );
          case "NotExists":
            return t(
              "warnings.MoreThanFiveOwnersFiveYears.content_not_exists",
              "Pojazd miał stabilną historię właścicieli, co może sugerować brak ukrytych problemów."
            );
          case "MissingData":
            return t(
              "warnings.MoreThanFiveOwnersFiveYears.content_missing_data",
              "Brak kompletnych danych o historii właścicieli pojazdu."
            );
        }
      }
      switch (status) {
        case "Exists":
          return "Pojazd miał wielu właścicieli w krótkim czasie. Może to wskazywać na ukrywane problemy techniczne lub prawne.";
        case "NotExists":
          return "Pojazd miał stabilną historię właścicieli, co może sugerować brak ukrytych problemów.";
        case "MissingData":
          return "Brak kompletnych danych o historii właścicieli pojazdu.";
      }
    },
    getRecommendation: (status, t?) => {
      if (t) {
        switch (status) {
          case "Exists":
            return t(
              "warnings.MoreThanFiveOwnersFiveYears.recommendation_exists",
              "Zapytaj o przyczynę częstych zmian właścicieli i dokładnie sprawdź stan techniczny."
            );
          case "NotExists":
            return undefined;
          case "MissingData":
            return t(
              "warnings.MoreThanFiveOwnersFiveYears.recommendation_missing_data",
              "Spróbuj uzyskać pełną historię od poprzednich właścicieli lub z systemu rejestracji."
            );
        }
      }
      switch (status) {
        case "Exists":
          return "Zapytaj o przyczynę częstych zmian właścicieli i dokładnie sprawdź stan techniczny.";
        case "NotExists":
          return undefined;
        case "MissingData":
          return "Spróbuj uzyskać pełną historię od poprzednich właścicieli lub z systemu rejestracji.";
      }
    },
  },
  CommercialUsageDetected: {
    getTitle: (status: ProblemStatus, t?: TranslationFunction) => {
      if (t) {
        switch (status) {
          case "Exists":
            return t(
              "warnings.CommercialUsageDetected.title_exists",
              "Wykryto użytkowanie komercyjne"
            );
          case "NotExists":
            return t(
              "warnings.CommercialUsageDetected.title_not_exists",
              "Pojazd był użytkowany prywatnie"
            );
          case "MissingData":
            return t(
              "warnings.CommercialUsageDetected.title_missing_data",
              "Brak wystarczających danych do weryfikacji czy pojazd był użytkowany komercyjnie."
            );
        }
      }
      switch (status) {
        case "Exists":
          return "Wykryto użytkowanie komercyjne";
        case "NotExists":
          return "Pojazd był użytkowany prywatnie";
        case "MissingData":
          return "Brak wystarczających danych do weryfikacji czy pojazd był użytkowany komercyjnie.";
      }
    },
    getContent: (status, t?) => {
      if (t) {
        switch (status) {
          case "Exists":
            return t(
              "warnings.CommercialUsageDetected.content_exists",
              "Pojazd był wykorzystywany do celów komercyjnych (taxi, wynajem, flotowy). Zazwyczaj oznacza to intensywniejszą eksploatację."
            );
          case "NotExists":
            return t(
              "warnings.CommercialUsageDetected.content_not_exists",
              "Pojazd był użytkowany prywatnie, co zazwyczaj wiąże się z lepszym stanem technicznym."
            );
          case "MissingData":
            return t(
              "warnings.CommercialUsageDetected.content_missing_data",
              "Brak informacji o charakterze użytkowania pojazdu."
            );
        }
      }
      switch (status) {
        case "Exists":
          return "Pojazd był wykorzystywany do celów komercyjnych (taxi, wynajem, flotowy). Zazwyczaj oznacza to intensywniejszą eksploatację.";
        case "NotExists":
          return "Pojazd był użytkowany prywatnie, co zazwyczaj wiąże się z lepszym stanem technicznym.";
        case "MissingData":
          return "Brak informacji o charakterze użytkowania pojazdu.";
      }
    },
    getRecommendation: (status, t?) => {
      if (t) {
        switch (status) {
          case "Exists":
            return t(
              "warnings.CommercialUsageDetected.recommendation_exists",
              "Zwróć szczególną uwagę na stan techniczny i przebieg. Rozważ dokładną ekspertyzę mechaniczną."
            );
          case "NotExists":
            return undefined;
          case "MissingData":
            return t(
              "warnings.CommercialUsageDetected.recommendation_missing_data",
              "Zapytaj sprzedawcę o sposób użytkowania i sprawdź dokumentację serwisową."
            );
        }
      }
      switch (status) {
        case "Exists":
          return "Zwróć szczególną uwagę na stan techniczny i przebieg. Rozważ dokładną ekspertyzę mechaniczną.";
        case "NotExists":
          return undefined;
        case "MissingData":
          return "Zapytaj sprzedawcę o sposób użytkowania i sprawdź dokumentację serwisową.";
      }
    },
  },
};

/**
 * Mapuje severity na variant komponentu SectionSummaryHSP
 */
const mapSeverityToVariant = (
  severity: RiskSeverity,
  status: ProblemStatus
): SectionSummaryHSPVariant => {
  // Jeśli problem nie istnieje, zawsze pokazuj jako success
  if (status === "NotExists") {
    return "success";
  }

  // Jeśli brak danych, pokazuj jako warning
  if (status === "MissingData") {
    return "warning";
  }

  // Jeśli problem istnieje, mapuj według severity
  switch (severity) {
    case "High":
      return "danger";
    case "Medium":
      return "warning";
    case "Low":
      return "success";
  }
};

/**
 * Mapuje pojedynczy element ostrzeżenia z API na format wymagany przez komponent
 */
const mapWarningItem = (
  item: WarningItem,
  t?: TranslationFunction
): MappedWarning | null => {
  const details = warningCodeMap[item.code];

  // Jeśli nie mamy szczegółów dla tego kodu, pomijamy
  if (!details) {
    console.warn(`Unknown warning code: ${item.code}`);
    return null;
  }

  return {
    variant: mapSeverityToVariant(item.severity, item.problemStatus),
    title: details.getTitle(item.problemStatus, t),
    content: details.getContent(item.problemStatus, t),
    recommendation: details.getRecommendation(item.problemStatus, t),
    severity: item.severity,
    problemStatus: item.problemStatus,
  };
};

/**
 * Mapuje wszystkie ostrzeżenia i rekomendacje z API
 * Sortuje według ważności: Exists > MissingData > NotExists
 */
export const mapWarningsAndRecommendations = (
  data?: WarningsAndRecommendations,
  t?: TranslationFunction
): MappedWarning[] => {
  if (!data) {
    return [];
  }

  const allWarnings: MappedWarning[] = [];

  // Zbierz wszystkie ostrzeżenia ze wszystkich poziomów severity
  const allItems = [
    ...data.items.High,
    ...data.items.Medium,
    ...data.items.Low,
  ];

  // Mapuj każde ostrzeżenie
  allItems.forEach((item) => {
    const mapped = mapWarningItem(item, t);
    if (mapped) {
      allWarnings.push(mapped);
    }
  });

  // Sortuj według priorytetu:
  // 1. Najpierw problemStatus: Exists > MissingData > NotExists
  // 2. Potem severity: High > Medium > Low
  const statusPriority: Record<ProblemStatus, number> = {
    Exists: 1,
    MissingData: 2,
    NotExists: 3,
  };

  const severityPriority: Record<RiskSeverity, number> = {
    High: 1,
    Medium: 2,
    Low: 3,
  };

  allWarnings.sort((a, b) => {
    const statusDiff =
      statusPriority[a.problemStatus] - statusPriority[b.problemStatus];
    if (statusDiff !== 0) return statusDiff;

    return severityPriority[a.severity] - severityPriority[b.severity];
  });

  return allWarnings;
};

/**
 * Tworzy dane ostrzeżeń i rekomendacji na podstawie danych z API
 */
export const createWarningsData = (
  data?: WarningsAndRecommendations,
  t?: TranslationFunction
): MappedWarning[] => {
  return mapWarningsAndRecommendations(data, t);
};
